import { useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Mail, MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";
import { LEGAL } from "@/config/legal";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent — we'll be in touch soon.");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="scent-wave min-h-[calc(100vh-4rem)]">
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-2 gap-16">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Get in touch</div>
          <h1 className="mt-3 font-serif text-5xl md:text-6xl tracking-tight text-[#0A1128] leading-[1.05]">
            Let&apos;s <span className="italic text-emerald-600">talk</span>.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-md">
            Hosts, investors, hospitality partners, press — we read every message.
          </p>

          <div className="mt-10 space-y-5 text-sm text-slate-700">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500 font-semibold">Founder</div>
                <div className="mt-1 font-medium text-[#0A1128]">Chalounda Williams</div>
                <div className="text-slate-500 text-xs">Replies come from the Guest Smell™ founder inbox</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#F9F3E5] flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-[#0A1128]" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500 font-semibold">Coverage</div>
                <div className="mt-1 font-medium text-[#0A1128]">Global</div>
                <div className="text-slate-500 text-xs">Launching first in select US metros and Kyoto</div>
              </div>
            </div>
            <div className="flex items-start gap-3" data-testid="contact-business-block">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                <Building2 className="w-4 h-4 text-slate-700" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500 font-semibold">Business</div>
                <div className="mt-1 font-medium text-[#0A1128]">{LEGAL.brandMark}</div>
                <div className="text-slate-500 text-xs">Registered in {LEGAL.address.state}, {LEGAL.address.country}</div>
                <div className="text-slate-500 text-xs mt-0.5">
                  <a href={`mailto:${LEGAL.legalEmail}`} className="text-emerald-700 hover:underline">{LEGAL.legalEmail}</a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          {done ? (
            <div className="rounded-3xl bg-white border border-emerald-200 p-10 text-center" data-testid="contact-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
              <h3 className="mt-4 font-serif text-3xl text-[#0A1128]">Message received.</h3>
              <p className="mt-2 text-slate-600 text-sm">Thanks — we&apos;ll be in touch within 1–2 business days.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl bg-white border border-slate-200 p-8">
              <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Send a message</div>
              <div className="mt-6 grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Full name</Label>
                  <Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="contact-name" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" required value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} data-testid="contact-email" />
                </div>
              </div>
              <div className="mt-4">
                <Label>Company (optional)</Label>
                <Input value={form.company} onChange={(e)=>setForm({...form, company: e.target.value})} data-testid="contact-company" />
              </div>
              <div className="mt-4">
                <Label>Message</Label>
                <Textarea rows={5} required value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})} data-testid="contact-message"
                  placeholder="Tell us about your property, your press deadline, or your investment thesis…" />
              </div>
              <Button type="submit" disabled={loading} data-testid="contact-submit"
                className="mt-6 rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white h-11 px-8">
                {loading ? "Sending…" : "Send message"}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
